import { useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { fetchAuthSession, signOut } from 'aws-amplify/auth';

/**
 * Upper bound on a single `fetchAuthSession()` call (issue #2595). On idle-return
 * Amplify silently refreshes tokens; if that fetch hangs, the Authenticator state
 * machine never transitions out of `'configuring'` and the app shows an unbounded
 * spinner. We race every session check against this timeout so a hang is always
 * resolved — in the worst case by sending the user to `/login`.
 */
export const SESSION_FETCH_TIMEOUT_MS = 8_000;

/**
 * Minimum hidden duration before a tab-return is treated as a real idle-return
 * (issue #2595). Without this, `fetchAuthSession()` would fire on every brief
 * refocus (tab flick, alt-tab) — wasteful and unnecessary. Only after the tab has
 * been hidden at least this long do we re-validate the session.
 */
export const IDLE_THRESHOLD_MS = 5 * 60 * 1_000;

/** Thrown when a `fetchAuthSession()` call exceeds {@link SESSION_FETCH_TIMEOUT_MS}. */
export class SessionTimeoutError extends Error {
    constructor() {
        super('Session fetch timed out');
        this.name = 'SessionTimeoutError';
    }
}

/**
 * Races a promise against a {@link SESSION_FETCH_TIMEOUT_MS} timeout, clearing the
 * timer on settle so no stray timer leaks. Rejects with {@link SessionTimeoutError}
 * if the timeout wins.
 */
function withTimeout<T>(promise: Promise<T>): Promise<T> {
    let timer: ReturnType<typeof setTimeout>;
    const timeout = new Promise<never>((_, reject) => {
        timer = setTimeout(() => reject(new SessionTimeoutError()), SESSION_FETCH_TIMEOUT_MS);
    });
    return Promise.race([promise, timeout]).finally(() => clearTimeout(timer)) as Promise<T>;
}

/**
 * Always-mounted, render-nothing component that provides bounded auth-session
 * recovery (issue #2595).
 *
 * Renders no UI. It must be mounted INSIDE the always-present `<Router>` (it uses
 * `useNavigate`) and regardless of `authStatus`. It is deliberately NOT placed in
 * `useCurrentUser` / `AuthenticatedLayout`, which mount only after
 * `authStatus === 'authenticated'` — during the stuck `'configuring'` state those
 * components do not exist, so a recovery handler there would never run.
 *
 * Behavior:
 * - On mount, runs a bounded session check so a hung session-fetch at startup
 *   surfaces (worst case: the user is sent to `/login`, never an infinite spinner).
 * - On `visibilitychange` to `visible` after the tab was hidden at least
 *   {@link IDLE_THRESHOLD_MS}, re-validates the session, again bounded by the
 *   timeout. A short refocus does nothing.
 * - If the session check times out OR rejects, performs a local `signOut()` then
 *   navigates to `/login` (replacing, preserving the deep-link intent in
 *   `state.from`).
 *
 * Gating (issue #2579): recovery only fires when the session is plausibly
 * recoverable — `authStatus` is `'configuring'` (possibly a hung startup
 * refresh) or `'authenticated'` (an idle-return refresh that may now be
 * hanging). When Amplify has cleanly settled to `'unauthenticated'` (a
 * never-authenticated cold start), SessionRecovery STANDS DOWN: `ProtectedRoute`
 * already drives the `/login` redirect, and a competing `signOut()` + navigate
 * here would race that redirect and leave the URL stranded on the protected
 * path (the #2579 bug).
 */
function SessionRecovery() {
    const navigate = useNavigate();
    const location = useLocation();
    const { authStatus } = useAuthenticator((context) => [context.authStatus]);
    // Timestamp (ms) the tab last became hidden; null while visible.
    const hiddenSinceRef = useRef<number | null>(null);
    // Guards against concurrent recovery runs (e.g. mount check still in flight
    // when a visibilitychange fires).
    const checkingRef = useRef(false);
    // Always-current mirrors of authStatus + location so the once-only effect
    // below reads the latest values at recovery time without re-subscribing
    // listeners on every render.
    const authStatusRef = useRef(authStatus);
    authStatusRef.current = authStatus;
    const locationRef = useRef(location);
    locationRef.current = location;

    useEffect(() => {
        let cancelled = false;

        const recoverToLogin = async () => {
            try {
                await signOut();
            } catch (error) {
                // The session is already hung/failed — surface to login regardless.
                console.error('Error signing out during session recovery:', error);
            }
            if (!cancelled) {
                // Replace (no spurious history entry) and carry the deep-link
                // intent so post-login lands on the originally requested route —
                // matching ProtectedRoute's redirect contract (issue #2579).
                navigate('/login', { replace: true, state: { from: locationRef.current } });
            }
        };

        // True when Amplify has cleanly settled to `unauthenticated` (a
        // never-authenticated cold start). In that state ProtectedRoute owns the
        // /login redirect and SessionRecovery must stand down (issue #2579).
        // Read through a function so the value is re-evaluated on each call —
        // `authStatus` can transition while a bounded fetch is in flight.
        const isSettledUnauthenticated = () => authStatusRef.current === 'unauthenticated';

        const checkSession = async () => {
            // Stand down for a cleanly-settled unauthenticated cold start
            // (issue #2579). Only recover a plausibly-recoverable session
            // (configuring — possibly a hung startup refresh — or authenticated).
            if (isSettledUnauthenticated()) {
                return;
            }
            if (checkingRef.current) {
                return;
            }
            checkingRef.current = true;
            try {
                await withTimeout(fetchAuthSession());
                // Healthy session — nothing to do; the Authenticator drives the UI.
            } catch {
                // Re-check the gate: if Amplify settled to a clean
                // `unauthenticated` while the bounded fetch was in flight,
                // ProtectedRoute now owns the redirect — stand down (issue #2579).
                if (!cancelled && !isSettledUnauthenticated()) {
                    await recoverToLogin();
                }
            } finally {
                checkingRef.current = false;
            }
        };

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'hidden') {
                hiddenSinceRef.current = Date.now();
                return;
            }
            // Became visible. Only re-check after a real idle period.
            const hiddenSince = hiddenSinceRef.current;
            hiddenSinceRef.current = null;
            if (hiddenSince !== null && Date.now() - hiddenSince >= IDLE_THRESHOLD_MS) {
                void checkSession();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);

        // Bounded session check at mount so a hung startup fetch also surfaces.
        void checkSession();

        return () => {
            cancelled = true;
            document.removeEventListener('visibilitychange', handleVisibilityChange);
        };
        // navigate is stable across renders (react-router); run effect once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return null;
}

export default SessionRecovery;
